import { api, API } from "@/lib/api";
import { BUILD_VERSION } from "@/lib/buildVersion";
import { arePricesVisible } from "@/hooks/usePriceVisibility";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

// When a trader has flipped on Presentation Mode on this device they're
// browsing the catalog with a retail customer at their side; we omit the
// price line from the outgoing enquiry text so the retail customer (who
// might glance at the phone before the trader sends) doesn't see the
// wholesale price either. The visibility flag is per-device (localStorage).
const includePriceLine = () => arePricesVisible();

// Strip everything except digits — wa.me wants a bare phone number
const normalizePhone = (raw) => (raw || "").replace(/[^\d]/g, "");

/** Public image URL (no auth) — kept for legacy uses (the image bytes endpoint). */
export const publicCardImageUrl = (cardId) => `${API}/public/cards/${cardId}/image`;
export const publicSeriesImageUrl = (seriesId) => `${API}/public/series/${seriesId}/image`;

/** Public Open Graph preview pages — WhatsApp's link-preview crawler reads
 * these and renders a rich card with the product image as a thumbnail right
 * inside the chat. The `?v=BUILD_VERSION` query param forces WhatsApp to
 * re-scrape after a deploy (otherwise it keeps a stale "no preview" cache
 * from before the OG endpoint existed for up to 24 hours per URL). */
export const publicCardPreviewUrl = (cardId) => `${API}/p/${cardId}?v=${BUILD_VERSION}`;
export const publicSeriesPreviewUrl = (seriesId) => `${API}/p/s/${seriesId}?v=${BUILD_VERSION}`;

let cachedAdminPhone = null;
export async function getAdminWhatsApp() {
  if (cachedAdminPhone) return cachedAdminPhone;
  try {
    // Use the public settings route so unauthenticated retail visitors can also
    // open WhatsApp inquiries. Auth'd users hit the same route — admin_whatsapp
    // is identical for both audiences.
    const { data } = await api.get("/settings/public");
    cachedAdminPhone = data.admin_whatsapp || null;
  } catch {
    cachedAdminPhone = null;
  }
  return cachedAdminPhone;
}

export function clearAdminWhatsAppCache() {
  cachedAdminPhone = null;
}

/** Open the customer's WhatsApp pre-filled to chat with the admin about a specific card. */
export async function shareCardOnWhatsApp(card, customer) {
  const phone = await getAdminWhatsApp();
  if (!phone) {
    return { ok: false, error: "Admin WhatsApp number is not configured yet." };
  }
  const lines = [
    "Namaste,",
    "",
    `I'd like to enquire about this card from *Shubh Vivah* (by Paper Kraft):`,
    "",
    `*${card.name}*`,
    card.code ? `Code: ${card.code}` : null,
    includePriceLine() ? `Price: ${formatINR(card.price)} per piece` : null,
    `Availability: ${card.available ? "In Stock" : "Sold Out"}`,
    card.category ? `Category: ${card.category}` : null,
    "",
    publicCardPreviewUrl(card.id),
    "",
    `— ${customer?.business_name || customer?.name || "Customer"}` + (customer?.phone ? ` (${customer.phone})` : ""),
  ].filter(Boolean);
  const text = lines.join("\n");
  const url = `https://wa.me/${normalizePhone(phone)}?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  return { ok: true };
}

/** Open WhatsApp pre-filled with a full order summary. */
export async function shareOrderOnWhatsApp(order) {
  const phone = await getAdminWhatsApp();
  if (!phone) {
    return { ok: false, error: "Admin WhatsApp number is not configured yet." };
  }
  const itemLines = order.items.flatMap((it) => [
    `• *${it.card_name}*  ×${it.quantity}  =  ${formatINR(it.price * it.quantity)}`,
    publicCardPreviewUrl(it.card_id),
  ]);
  const lines = [
    `🎴 *New Order — Shubh Vivah*`,
    `Order #${order.id.slice(0, 8)}`,
    "",
    ...itemLines,
    "",
    `Total: *${formatINR(order.total)}*`,
    order.notes ? `Note: ${order.notes}` : null,
    "",
    `— ${order.customer_business || order.customer_name}`,
  ].filter(Boolean);
  const text = lines.join("\n");
  const url = `https://wa.me/${normalizePhone(phone)}?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  return { ok: true };
}

/** Enquire about an entire series on WhatsApp. */
export async function shareSeriesOnWhatsApp(series, cards, customer) {
  const phone = await getAdminWhatsApp();
  if (!phone) return { ok: false, error: "Admin WhatsApp number is not configured yet." };
  const codeList = (cards || []).map((c) => c.code).filter(Boolean);
  const lines = [
    "Namaste,",
    "",
    `I'd like to enquire about this *whole series* from *Shubh Vivah* (by Paper Kraft):`,
    "",
    `*${series.name}*`,
    series.code ? `Series Code: ${series.code}` : null,
    includePriceLine() ? `Price: ${formatINR(series.price)} per piece` : null,
    series.category ? `Category: ${series.category}` : null,
    `Designs in series: ${cards?.length ?? 0}`,
    codeList.length ? `Codes: ${codeList.join(", ")}` : null,
    "",
    publicSeriesPreviewUrl(series.id),
    "",
    `— ${customer?.business_name || customer?.name || "Customer"}` + (customer?.phone ? ` (${customer.phone})` : ""),
  ].filter(Boolean);
  const text = lines.join("\n");
  const url = `https://wa.me/${normalizePhone(phone)}?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  return { ok: true };
}


/** Open WhatsApp pre-filled with the customer's full wishlist of saved designs.
 * `items` is the array stored by SavedDesignsContext — each entry has the minimum
 * fields needed to render a useful line and a clickable public image URL. */
export async function shareSavedDesignsOnWhatsApp(items, customer) {
  const phone = await getAdminWhatsApp();
  if (!phone) return { ok: false, error: "Admin WhatsApp number is not configured yet." };
  if (!items || !items.length) return { ok: false, error: "No saved designs to share." };

  // Build per-design blocks: name + code + price + a public image URL on its own
  // line so WhatsApp generates a thumbnail preview for each one.
  const blocks = items.flatMap((it, idx) => [
    `${idx + 1}. *${it.name}*${it.code && it.code !== it.name ? `  (${it.code})` : ""}`,
    it.price != null ? `   Price: ${formatINR(it.price)} per piece` : null,
    `   ${publicCardPreviewUrl(it.id)}`,
    "",
  ].filter(Boolean));

  const lines = [
    "Namaste,",
    "",
    `I'd like to enquire about these *${items.length} design${items.length === 1 ? "" : "s"}* I shortlisted on *Shubh Vivah* (by Paper Kraft):`,
    "",
    ...blocks,
    `— ${customer?.business_name || customer?.name || "Customer"}` + (customer?.phone ? ` (${customer.phone})` : ""),
  ];
  const text = lines.join("\n");
  const url = `https://wa.me/${normalizePhone(phone)}?text=${encodeURIComponent(text)}`;
  window.open(url, "_blank", "noopener,noreferrer");
  return { ok: true };
}
